/**
 * V2 API Interceptor with JWT Authentication
 * V1 호환성 없이 V2만 지원
 */

import { getFirebaseAuth } from '@/config/firebase.config'

export class ApiInterceptor {
  private accessToken: string | null = null
  private refreshToken: string | null = null
  private baseUrl: string
  private apiKey: string | null = null

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_WEPIN_BACKEND_V2_URL
    this.apiKey = import.meta.env.VITE_X_API_KEY || null
    this.loadTokens()
  }

  /**
   * 로컬 스토리지에서 토큰 로드
   */
  private loadTokens(): void {
    this.accessToken = localStorage.getItem('accessToken')
    this.refreshToken = localStorage.getItem('refreshToken')
  }

  /**
   * 토큰 저장
   */
  public saveTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken
    this.refreshToken = refreshToken
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
  }

  /**
   * Firebase ID 토큰 가져오기
   */
  private async getFirebaseIdToken(): Promise<string | null> {
    const auth = getFirebaseAuth()
    if (!auth || !auth.currentUser) {
      return null
    }
    try {
      return await auth.currentUser.getIdToken()
    } catch {
      return null
    }
  }

  /**
   * API 요청 실행 (V2 전용)
   * JWT 토큰과 x-api-key를 항상 사용
   */
  async request(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`

    // 기본 헤더 설정
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
      version: 'v2',
    }

    // FormData가 아닌 경우에만 Content-Type 설정 (FormData는 브라우저가 자동 설정)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    // JWT 토큰 추가 (로그인 전 public 엔드포인트는 제외)
    const publicEndpoints = [
      '/auth/login', // 로그인
      '/auth/refresh', // 토큰 갱신
      '/user/login', // V1 로그인
      '/user/exist', // 사용자 존재 확인
      '/user/check-email', // 이메일 확인
      '/user/oauth-providers', // OAuth 프로바이더 목록
      '/oauth/', // OAuth 관련 모든 경로
      '/droplink/', // Droplink 캠페인 관련
      '/project/', // 프로젝트 정보 조회
    ]

    const isPublicEndpoint = publicEndpoints.some(
      (path) => endpoint === path || endpoint.startsWith(path),
    )

    if (this.accessToken && !isPublicEndpoint) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    // x-api-key 추가 (모든 V2 요청에 포함)
    if (this.apiKey) {
      headers['x-api-key'] = this.apiKey
    }

    options.headers = headers

    let response = await fetch(url, options)

    // 401 에러 시 토큰 갱신 시도 (한 번만)
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken()
      if (refreshed && this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`
        options.headers = headers
        response = await fetch(url, options)
      }
    }

    return response
  }

  /**
   * Access Token 갱신 (V2 전용)
   * POST /auth/refresh with body { refreshToken }
   */
  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        version: 'v2',
      }

      // x-api-key 추가
      if (this.apiKey) {
        headers['x-api-key'] = this.apiKey
      }

      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ refreshToken: this.refreshToken }),
      })

      if (response.ok) {
        const data = await response.json()
        // V2 응답 구조: { success: true, data: { accessToken: "..." } }
        const newAccessToken = data.data?.accessToken || data.accessToken
        if (newAccessToken) {
          this.accessToken = newAccessToken
          localStorage.setItem('accessToken', newAccessToken)
          return true
        }
        return false
      } else {
        // Refresh token도 만료된 경우에만 로그아웃
        if (response.status === 401) {
          this.logout()
        }
        return false
      }
    } catch {
      return false
    }
  }

  /**
   * 로그아웃 - 토큰 제거
   */
  public logout(): void {
    this.accessToken = null
    this.refreshToken = null
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')

    // 로그인 페이지로 리다이렉트
    if (window.location.pathname !== '/signin') {
      window.location.href = '/signin'
    }
  }

  /**
   * Firebase 인증으로 로그인 (V2 전용)
   */
  public async loginWithFirebase(): Promise<{
    token: string
    refreshToken: string
  } | null> {
    const firebaseToken = await this.getFirebaseIdToken()
    if (!firebaseToken) {
      throw new Error('Firebase authentication required')
    }

    const response = await this.request('/user/login', {
      method: 'POST',
      body: JSON.stringify({ idToken: firebaseToken }),
    })

    if (response.ok) {
      const data = await response.json()
      this.saveTokens(data.token, data.refreshToken)
      return data
    }

    return null
  }

  /**
   * 현재 토큰 상태 확인
   */
  public hasValidToken(): boolean {
    return Boolean(this.accessToken)
  }

  /**
   * 현재 액세스 토큰 가져오기
   */
  public getAccessToken(): string | null {
    return this.accessToken
  }
}

// 싱글톤 인스턴스
export const apiInterceptor = new ApiInterceptor()
